<template>
  <div class="snackbar-notification" :class="{ show }">
    <div class="snackbar-notification__text">
      <TitleSmall :title="title" />
      <TextSmall>{{ message }}</TextSmall>
      <BtnNormal
        light-theme
        :title="$t('label.notification.open_conversation')"
        @click="handleOpenNotification"
      />
    </div>
    <button class="snackbar-notification__close" @click="close">
      <i class="fas fa-times" />
    </button>
  </div>
</template>
<script setup>
import BtnNormal from '@/components/shared/btn/BtnNormal'
import TextSmall from '@/components/shared/font/text/TextSmall'
import TitleSmall from '@/components/shared/font/title/TitleSmall'
import i18n from '@/i18n'
import { $cookies } from '@/main'
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'

const store = useStore()
const $t = i18n.t
const route = useRoute()
const router = useRouter()

const time = ref(null)
const channelId = ref(null)

const notification = computed(() => store.state.chatNotification.notification)
const showNotification = computed(
  () => store.state.chatNotification.showNotification
)
const customerConversation = computed(
  () => store.state.chat.currentConversation
)
const supplierConversation = computed(
  () => store.state.supplierChat.currentConversation
)

const title = computed(() => notification.value && notification.value.title)
const message = computed(() => notification.value && notification.value.message)
const show = computed(() => showNotification.value)

const closeNotification = () =>
  store.commit('chatNotification/closeNotification')
const setCustomerConversation = (payload) =>
  store.dispatch('chat/setCurrentConversationById', payload)
const setSupplierConversation = (payload) =>
  store.dispatch('supplierChat/setCurrentConversationById', payload)
const startLoader = () => store.dispatch('loading/startLoader')
const stopLoader = () => store.dispatch('loading/stopLoader')

function close() {
  clearTimeout(time.value)
  closeNotification()
}

async function handleOpenNotification() {
  closeNotification()
  startLoader()
  try {
    if ($cookies.get('userType') !== 'supplier') {
      await setCustomerConversation(channelId.value)
    } else {
      await setSupplierConversation(channelId.value)
    }
  } finally {
    if (
      supplierConversation.value.attributes ||
      customerConversation.value.attributes
    ) {
      if (route.name !== 'customerChat' && route.name !== 'supplierChat') {
        if ($cookies.get('userType') !== 'supplier') {
          stopLoader()
          router.push({
            name: 'customerChat',
            query: {
              view: customerConversation.value.attributes.type
            }
          })
        } else {
          stopLoader()
          router.push({
            name: 'supplierChat',
            query: {
              ...route.query,
              view: supplierConversation.value.attributes.type
            }
          })
        }
      } else {
        if ($cookies.get('userType') !== 'supplier') {
          router.replace({
            query: {
              view: customerConversation.value.attributes.type
            }
          })
        } else {
          router.replace({
            query: {
              ...route.query,
              view: supplierConversation.value.attributes.type
            }
          })
        }
      }
    }
    stopLoader()
  }
  channelId.value = null
}

watch(
  () => show.value,
  (newValue) => {
    if (newValue) {
      time.value = setTimeout(() => {
        closeNotification()
      }, 10000)
    } else {
      clearTimeout(time.value)
    }
  }
)

watch(
  () => notification.value,
  () => {
    if (notification.value.channelId) {
      channelId.value = notification.value.channelId
    }
  }
)

onBeforeUnmount(() => {
  stopLoader()
})
</script>
<style lang="scss" scoped>
.snackbar-notification {
  visibility: hidden;
  min-width: 250px;
  box-shadow: 0px 2px 5px $black-30;
  padding: 0.7rem 1.5rem;
  position: absolute;
  z-index: 800;
  top: 15px;
  border: 1px solid;
  border-radius: 3px;
  max-width: 700px;
  display: flex;
  right: 15px;
  border-color: $blue-80;
  background-color: $white;

  @include mobileLandscape {
    min-width: 500px;
  }

  &__text {
    .titleSmall {
      font-size: 15px !important;
    }
  }
  &__close {
    all: unset;
    position: absolute;
    top: 10px;
    right: 20px;
    color: $black-50;
    cursor: pointer;
    :hover {
      color: $black-80;
    }
  }
  .btn {
    margin-top: 10px;
    padding: 7px 10px;
    font-size: 14px;
    &__normal {
      &.light__theme {
        @include hover {
          background: $blue-80;
          color: $white;
          border-color: transparent;
        }
      }
    }
  }

  &.show {
    visibility: visible; /* Show the snackbar */
    -webkit-animation:
      fadein 0.5s,
      fadeout 0.5s 9.5s;
    animation:
      fadein 0.5s,
      fadeout 0.5s 9.5s;
  }
  // Animate the snackbar's transition from visibility states and position:
  @-webkit-keyframes fadein {
    from {
      top: -15px;
      opacity: 0;
    }
    to {
      top: 15px;
      opacity: 1;
    }
  }

  @keyframes fadein {
    from {
      top: -15px;
      opacity: 0;
    }
    to {
      top: 15px;
      opacity: 1;
    }
  }

  @-webkit-keyframes fadeout {
    from {
      top: 15px;
      opacity: 1;
    }
    to {
      top: -15px;
      opacity: 0;
    }
  }

  @keyframes fadeout {
    from {
      top: 15px;
      opacity: 1;
    }
    to {
      top: -15px;
      opacity: 0;
    }
  }
}
</style>
